import { createSelector } from 'reselect';
import { getFormValues } from 'redux-form';

import { filterByString } from '@topconagriculture/frontend-utils';

import { RootState } from '../../reducers';
import { Forms } from '../../form/form.selector';
import { Premix } from './premixes.types';
import { trimWhitespace } from '../../../core/helpers/utils/utils';
import { getIsEditMode } from '../../application/premixes/appPremixes.selector';

export const getPremixes = (state: RootState): Premix[] => state.entities.premixes;
export const getNumberOfPremixes = (state: RootState): number =>
  state.entities.premixes ? state.entities.premixes.length : 0;
export const getPremixesExists = (state: RootState): boolean =>
  state.entities.premixes && state.entities.premixes.length > 0;

const premixesFilterForm = getFormValues(Forms.premixesFilterForm);
const premixesNameFilterSelector = createSelector([premixesFilterForm], (values: any) => values?.rationName || '');
const premixesNotInUseSelector = createSelector([premixesFilterForm], (values: any) => values?.hideNotInUse);

export const getPremixesFiltered = createSelector(
  [getPremixes, premixesNameFilterSelector, premixesNotInUseSelector],
  (premixesList: Premix[], rationNameFilter: string, premixNotInUseFilter: boolean): Premix[] =>
    rationNameFilter || premixNotInUseFilter
      ? premixesList.filter((premix) => {
          const matchesUsage = premixNotInUseFilter ? !premix.notInUse : true;
          const matchesName =
            filterByString(premix.displayName, trimWhitespace(rationNameFilter)) ||
            filterByString(premix.description, trimWhitespace(rationNameFilter));

          return matchesUsage && matchesName;
        })
      : premixesList,
);

export const getPremixesFilteredToPremixAdd = createSelector(
  [
    getPremixesFiltered,
    getPremixes,
    getIsEditMode,
    (state: RootState, selectedPremixId: string | null) => selectedPremixId,
  ],
  (premixesList: Premix[], premixes, isEditMode, selectedPremixId: string | null): Premix[] => {
    const premixIngredientIds = premixes.map((premix) => premix.premixIngredientId);
    const selectedPremix = premixes.find((premix) => premix.id === selectedPremixId)!;
    return premixesList.filter(
      (premix) =>
        (isEditMode ? premix.id !== selectedPremixId : true) &&
        (isEditMode
          ? !isPremixInside(premixesList, premixIngredientIds, premix, selectedPremix.premixIngredientId)
          : true),
    );
  },
);

export const isPremix = createSelector(
  [getPremixes],
  (premixesList: Premix[]) =>
    (ingredientId: string): boolean =>
      !!premixesList.find((premix) => premix.premixIngredientId === ingredientId),
);

export const getPremixById = createSelector(
  [getPremixes],
  (premixList: Premix[]) =>
    (premixId: string | null): Premix | undefined => {
      return premixList.find((premix) => premix.id === premixId);
    },
);

export const isRationAPremixFn = (premixesList: Premix[], rationId: string): boolean =>
  !!premixesList.find((premix) => premix.id === rationId);

export const isRationAPremix = createSelector([getPremixes, (state, rationId: string) => rationId], isRationAPremixFn);

const isPremixInside = (
  premixesList: Premix[],
  premixIngredientIds: string[],
  premix: Premix,
  selectedPremixIngredientId: string,
) => {
  const ingredient = premix.ingredients.find((ing) => ing.ingredientId === selectedPremixIngredientId);
  if (!ingredient && premix.hasPremix) {
    return premix.ingredients.some((ing) => {
      const prem = premixesList.find((elem) => elem.id === ing.ingredientId);
      return prem ? isPremixInside(premixesList, premixIngredientIds, prem, selectedPremixIngredientId) : false;
    });
  }
  if (!ingredient || !premix.hasPremix) {
    return false;
  }
  if (ingredient) {
    return true;
  }
};
